import { useQuery } from '@tanstack/react-query';

import { statusQuery, type StatusOut } from '@/lib/api/queries';
import { useAsOf } from '@/lib/asof/use-asof';

/**
 * =============================================================================
 *  MODEL_OUTPUT_ACCESS（ADR-0002）在前端的**唯一**讀取點。
 *
 *  `GET /status` 的 `model_output_access` 決定模型頁的**導覽**要不要出現；
 *  頁面本身能不能看，永遠以各端點的回應為準（allowlist 時看得到與否依工作階段而定，
 *  直接貼網址進來的人一樣會拿到 404 `model_output_not_available`）。
 *
 *  「還不知道」與 off 同樣處理：/status 還沒回來（或讀不到）時不顯示模型導覽。
 *  先亮再熄會讓閘關著的站閃出一個模型連結——那正是 ADR-0002 A-D1 要防的「洩漏這裡其實有東西」。
 * ========================================================================== */

export type ModelOutputAccess = StatusOut['model_output_access'];

/** 導覽要不要顯示模型頁。undefined（/status 還沒回來或失敗）一律視為不顯示。 */
export function isModelOutputNavVisible(access: ModelOutputAccess | undefined): boolean {
  return access !== undefined && access !== 'off';
}

/**
 * registry 的 nav 裡 `category='model'` 的分組只在閘開著時出現。
 * 其餘分組（包括選股器所在的每日區塊，那不在 nav 裡）不受影響。
 */
export function visibleNavGroups<G extends { category: string }>(
  groups: readonly G[],
  access: ModelOutputAccess | undefined,
): G[] {
  return isModelOutputNavVisible(access) ? [...groups] : groups.filter((g) => g.category !== 'model');
}

/** `/status` 講的是設定，不是時序資料：刻意不帶 as_of（與 StatusPage 共用同一份快取）。 */
export function useModelOutputAccess(): ModelOutputAccess | undefined {
  const { today } = useAsOf();
  const status = useQuery(statusQuery(undefined, today));
  return status.data?.model_output_access;
}
